import type { ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { ChevronLeftIcon } from '@/icons';
import { Button, IconButton } from '@/ui_v3';
import { usePathParentLink } from '../hooks/usePathParentLink';
import type { ParentLinkFallback } from '../utils/navPath';

/** 소유를 아직 모를 때 같은 크기의 보이지 않는 자리. */
const PendingSlot = ({
  children
}: {
  children: ReactNode;
}) => <div aria-hidden className={'invisible'} data-sentry-component="PendingSlot" data-sentry-source-file="PathParentLink.tsx">
    {children}
  </div>;

/** 목록 버튼 — 걸어온 길의 부모 칸으로 간다. `fallback` 은 부모 칸이 없을 때 갈 곳(없으면 소유 기준). */
export const PathParentLinkButton = ({
  placement = 'banner',
  fallback,
  className
}: {
  placement?: 'banner' | 'plain';
  fallback?: ParentLinkFallback;
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const link = usePathParentLink(fallback);
  const isOnBanner = placement === 'banner';
  const buttonProps = {
    asChild: true,
    variant: isOnBanner ? 'contained' : 'transparent',
    intent: 'neutral',
    size: isOnBanner ? 'sm' : 'md',
    rounded: 'default',
    className: className ?? (isOnBanner ? 'shadow-none' : undefined)
  } as const;
  if (link.status === 'pending') {
    return <PendingSlot>
        <Button {...buttonProps}>
          <span>
            <ChevronLeftIcon size="sm" fill={ICON_COLOR.neutral} />
            {t('common:quizDetail.browseExplore')}
          </span>
        </Button>
      </PendingSlot>;
  }
  return <Button {...buttonProps} data-sentry-element="Button" data-sentry-component="PathParentLinkButton" data-sentry-source-file="PathParentLink.tsx">
      <Link {...link.getLinkProps(placement)} data-sentry-element="Link" data-sentry-source-file="PathParentLink.tsx">
        <ChevronLeftIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="ChevronLeftIcon" data-sentry-source-file="PathParentLink.tsx" />
        {link.label}
      </Link>
    </Button>;
};

/** 스크롤 헤더의 아이콘 목록 버튼. */
export const PathParentLinkIconButton = () => {
  const link = usePathParentLink();
  const iconButtonProps = {
    asChild: true,
    variant: 'transparent',
    intent: 'neutral',
    size: 'sm',
    className: 'shrink-0'
  } as const;
  if (link.status === 'pending') {
    return <PendingSlot>
        <IconButton {...iconButtonProps}>
          <span>
            <ChevronLeftIcon size="sm" fill={ICON_COLOR.neutral} />
          </span>
        </IconButton>
      </PendingSlot>;
  }
  return <IconButton {...iconButtonProps} aria-label={link.label} data-sentry-element="IconButton" data-sentry-component="PathParentLinkIconButton" data-sentry-source-file="PathParentLink.tsx">
      <Link {...link.getLinkProps('header_icon')} data-sentry-element="Link" data-sentry-source-file="PathParentLink.tsx">
        <ChevronLeftIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="ChevronLeftIcon" data-sentry-source-file="PathParentLink.tsx" />
      </Link>
    </IconButton>;
};